import { useState } from 'react'
import { listInvoices, deleteInvoice, duplicateInvoice, invoicePdfUrl, invoicePdfTabId, sendSelectedInvoices, getSendPreviewSelected, type Invoice, type MailOverrides } from '../api'
import SendMailDrawer from '../components/SendMailDrawer'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useAuth } from '../layout/AuthContext'
import { useToast } from '../layout/ToastContext'
import { brandName, BRANDS, formatMoney, invoiceStatusLabel, invoiceTabName } from '../lookups'
import { fill, INVOICE_STATUS_LABELS } from '../../shared/texts'
import { useServerList } from '../hooks/useServerList'
import { useColumnVisibility } from '../hooks/useColumnVisibility'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import Pagination from '../components/Pagination'
import DataTable, { type Column } from '../components/DataTable'
import ColumnManager from '../components/ColumnManager'
import ConfirmDialog from '../components/ConfirmDialog'
import { MONEY_EPSILON, DEFAULT_TABLE_PAGE_SIZE } from '../../shared/constants'
import { PAGES_INVOICES_LIST_TEXTS as T } from '../../shared/texts'

const EditIcon = () => <img src="/icons/edit.svg" alt="" aria-hidden="true" />
const DeleteIcon = () => <img src="/icons/delete.svg" alt="" aria-hidden="true" />
const DuplicateIcon = () => <img src="/icons/duplicate.svg" alt="" aria-hidden="true" />
const PrinterIcon = () => <img src="/icons/printer.svg" alt="" aria-hidden="true" />

// The one formatter (constants.ts): the record's currency, else the backoffice currency.
const money = (n: number, currency?: string) => formatMoney(n, currency)

// Aus der einen Tabelle gebaut, statt die Namen ein zweites Mal hinzuschreiben — sonst heisst
// derselbe Zustand im Filter anders als in der Spalte daneben.
const STATUSES = Object.entries(INVOICE_STATUS_LABELS).map(([key, name]) => ({ key, name }))

export default function InvoicesList() {
    const { openTab, publishChange } = useTabs()
    const version = useDataVersion('invoices') // refetch after InvoiceForm creates/finalizes one
    const { preferences, savePreferences, can } = useAuth()
    const canWrite = can('invoices', 'write')
    const canDelete = can('invoices', 'delete')

    const [status, setStatus] = useState('')
    const [brand, setBrand] = useState('')
    const list = useServerList<Invoice>(listInvoices, version, {
        sort: 'createdAt', dir: 'desc', pageSize: preferences.tablePageSize?.invoices ?? DEFAULT_TABLE_PAGE_SIZE,
        params: { status: status || undefined, brand: brand || undefined },
    })
    const cols = useColumnVisibility('invoices')

    const onPageSize = (n: number) => {
        list.setPageSize(n)
        savePreferences({ tablePageSize: { ...(preferences.tablePageSize ?? {}), invoices: n } })
    }

    // Open an invoice: drafts are editable (with write right), others view-only.
    const openEdit = (inv: Invoice) => openTab({
        id: `invoice-${inv._id}`,
        name: invoiceTabName(inv),
        component: 'invoice-form',
        config: { id: inv._id },
    })

    // „Rechnung duplizieren" (operator, 31.10.2026): new draft, opened right away in the editor.
    // Not for a credit note and not for a cancelled invoice (39a/40) — the server refuses those too.
    const duplicable = (inv: Invoice) => canWrite && !inv.creditNoteFor && (inv.credited ?? 0) <= MONEY_EPSILON
    const [duplicating, setDuplicating] = useState<string | null>(null)
    const duplicate = async (inv: Invoice) => {
        setDuplicating(inv._id)
        try {
            const draft = await duplicateInvoice(inv._id)
            publishChange('invoices')
            openEdit(draft)
        } catch (e: any) { toast(e?.response?.data?.error ?? e?.message ?? T.duplicateFailed, 'err') }
        finally { setDuplicating(null) }
    }

    // Open the PDF in an in-app viewer tab (only finalized/sent invoices have one).
    // The tab carries the PDF's outgoing name (D1317-26103102.pdf), the same the viewer saves it under —
    // the address redirects to one ending on it (routes/invoices.ts).
    const openPdf = (inv: Invoice) => openTab({
        id: invoicePdfTabId(inv._id),
        name: inv.pdfName ?? (inv.invoiceNumber ? fill(T.pdfTabName, { invoiceNumber: inv.invoiceNumber }) : T.pdfTabFallback),
        component: 'file-viewer',
        config: { url: invoicePdfUrl(inv._id, inv.pdfName), fname: inv.pdfName ?? fill(T.pdfFileName, { name: inv.invoiceNumber ?? inv._id }) },
    })

    // Delete a draft (confirmed).
    const [del, setDel] = useState<Invoice | null>(null)
    const [delBusy, setDelBusy] = useState(false)
    const [delError, setDelError] = useState('')
    async function confirmDelete() {
        if (!del) return
        setDelBusy(true); setDelError('')
        try { await deleteInvoice(del._id); setDel(null); publishChange('invoices') }
        catch (e: any) { setDelError(e?.response?.data?.error ?? e?.message ?? T.deleteFailed) }
        finally { setDelBusy(false) }
    }

    const hasPdf = (inv: Invoice) => inv.status === 'finalized' || inv.status === 'sent'

    // Collective dispatch of hand-picked documents. Kept across pages and filters (by id), so the
    // operator can collect a customer's invoices from anywhere in the list. ONE customer only —
    // the server refuses anything else; the boxes of other customers are only greyed out here.
    const { toast } = useToast()
    const [selected, setSelected] = useState<Map<string, Invoice>>(new Map())
    const [sendOpen, setSendOpen] = useState(false)
    const picked = [...selected.values()].sort((a, b) => (a.referenceNumber ?? 0) - (b.referenceNumber ?? 0))
    const lockedCustomer = picked[0]?.customerId
    const whyNotSelectable = (inv: Invoice): string => {
        if (inv.status !== 'finalized' && inv.status !== 'sent') return T.onlyFinalized
        if ((inv.credited ?? 0) > MONEY_EPSILON) return T.cancelledNotSent
        if (lockedCustomer && inv.customerId !== lockedCustomer) return T.sameCustomerOnly
        return ''
    }
    const toggle = (inv: Invoice) => setSelected((prev) => {
        const next = new Map(prev)
        if (next.has(inv._id)) next.delete(inv._id); else next.set(inv._id, inv)
        return next
    })
    // Errors are thrown on to the send drawer, which shows them and stays open.
    async function doSendSelected(overrides: MailOverrides) {
        const r = await sendSelectedInvoices(picked.map((i) => i._id), overrides)
        setSendOpen(false); setSelected(new Map())
        publishChange('invoices')
        toast(r.blocked ? r.blocked
            : r.sent && r.redirectedTo ? fill(T.sentRedirected, { redirectedTo: r.redirectedTo, to: r.to, numbers: r.invoiceNumbers.join(', ') })
            : r.sent
            ? fill(T.sentTo, { to: r.to, numbers: r.invoiceNumbers.join(', ') })
            : fill(T.sentPartly, { numbers: r.mails.filter((m) => m.sent).flatMap((m) => m.invoiceNumbers).join(', ') || T.sentNothing }),
        r.sent ? 'ok' : 'err')
    }

    const columns: Column<Invoice>[] = [
        ...(canWrite ? [{
            key: 'select', header: '', className: 'center select-col', defaultWidth: 32,
            render: (inv: Invoice) => {
                const why = selected.has(inv._id) ? '' : whyNotSelectable(inv)
                return <input type="checkbox" className="check" aria-label={T.selectForSend} checked={selected.has(inv._id)}
                    disabled={!!why} title={why || T.selectForSend} onChange={() => toggle(inv)} />
            },
        }] : []),
        { key: 'createdAt', header: T.colDate, sortField: 'createdAt', defaultWidth: 95, render: (inv) => new Date(inv.createdAt).toLocaleDateString('de-DE') },
        { key: 'customer', header: T.colCustomer, sortField: 'customer', defaultWidth: 180, render: (inv) => inv.billing?.company || inv.billing?.displayName || '—' },
        {
            key: 'invoiceNumber', header: T.colInvoiceNumber, sortField: 'invoiceNumber', defaultWidth: 130,
            render: (inv) => inv.invoiceNumber ?? <span className="muted">{T.draftMarker}</span>,
        },
        {
            key: 'referenceNumber', header: T.colReferenceNumber, sortField: 'referenceNumber', defaultWidth: 130,
            render: (inv) => inv.referenceNumber ?? <span className="muted">—</span>,
        },
        { key: 'gross', header: T.colAmount, sortField: 'gross', className: 'num', defaultWidth: 110, render: (inv) => money(inv.totals?.gross ?? 0, inv.currency) },
        // ⚠️ Die KLASSE bleibt der Rohwert (davon haengt die Farbe ab, `styles.css`), der TEXT
        // wird das Label. Beides in einem Ausdruck zu ersetzen war der Fehler, der hier stand.
        { key: 'status', header: T.status, sortField: 'status', className: 'center', defaultWidth: 100, render: (inv) => <span className={`badge ${inv.status}`}>{invoiceStatusLabel(inv.status)}</span> },
        { key: 'brand', header: T.brand, sortField: 'brand', defaultWidth: 120, render: (inv) => brandName(inv.brand) },
        {
            key: 'pdf', header: T.colPdf, className: 'center', defaultWidth: 70,
            render: (inv) => (
                <button className="icon-action" disabled={!hasPdf(inv)}
                    title={hasPdf(inv) ? T.openPdf : T.pdfAfterFinalize}
                    aria-label={T.openPdf} onClick={() => openPdf(inv)}><PrinterIcon /></button>
            ),
        },
        {
            key: 'actions', header: T.colActions, defaultWidth: 120, sticky: true,
            render: (inv) => {
                const deletable = inv.status === 'draft' && canDelete
                return (
                    <div className="row-actions">
                        <button className="icon-action" title={inv.status === 'draft' && canWrite ? T.edit : T.view}
                            aria-label={inv.status === 'draft' && canWrite ? T.edit : T.view} onClick={() => openEdit(inv)}><EditIcon /></button>
                        {duplicable(inv) && (
                            <button className="icon-action" title={T.duplicate} aria-label={T.duplicate}
                                disabled={duplicating !== null} onClick={() => duplicate(inv)}><DuplicateIcon /></button>
                        )}
                        <button className="icon-action" disabled={!deletable}
                            title={deletable ? T.delete : T.onlyDraftsDeletable}
                            aria-label={T.delete} onClick={() => setDel(inv)}><DeleteIcon /></button>
                    </div>
                )
            },
        },
    ]

    const filtered = list.q.trim() !== '' || status !== '' || brand !== ''

    return (
        <div className="list-page">
            <ListToolbar
                title={T.title}
                infoText={T.info}
                action={canWrite ? <>
                    {picked.length > 0 && <>
                        <button className="secondary" onClick={() => setSelected(new Map())}>{T.clearSelection}</button>
                        <button onClick={() => setSendOpen(true)}>{fill(T.sendSelected, { n: picked.length })}</button>
                    </>}
                    <button onClick={() => openTab({ id: 'invoice-new', name: T.newInvoiceTab, component: 'invoice-form' })}>{T.newInvoice}</button>
                </> : undefined}
            />

            {list.error && <div className="notice err">{list.error}</div>}

            <ListPanel
                count={list.total}
                grandTotal={list.grandTotal}
                filtered={filtered}
                search={{ value: list.q, onChange: list.setQ }}
                columnManager={<ColumnManager columns={columns} hidden={cols.hidden} onToggle={cols.toggle} onReset={cols.reset} />}
                filters={<>
                    <label>
                        {T.status}
                        <select value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="">{T.all}</option>
                            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                        </select>
                    </label>
                    <label>
                        {T.brand}
                        <select value={brand} onChange={(e) => setBrand(e.target.value)}>
                            <option value="">{T.all}</option>
                            {BRANDS.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                        </select>
                    </label>
                </>}
            >
                {list.loading ? <p className="muted">{T.loading}</p> : list.rows.length === 0 ? (
                    <p className="muted">{T.empty}</p>
                ) : (
                    <DataTable
                        key={cols.resetKey}
                        tableId="invoices"
                        columns={columns}
                        hidden={cols.hidden}
                        rows={list.rows}
                        rowKey={(inv) => inv._id}
                        sort={list.sort}
                        dir={list.dir}
                        onSort={list.toggleSort}
                        onRowActivate={openEdit}
                    />
                )}
            </ListPanel>

            <Pagination page={list.page} pageCount={list.pageCount} onPage={list.setPage} pageSize={list.pageSize} onPageSize={onPageSize} />

            <SendMailDrawer
                open={sendOpen}
                title={fill(T.sendSelected, { n: picked.length })}
                load={() => getSendPreviewSelected(picked.map((i) => i._id))}
                onSend={doSendSelected}
                onClose={() => setSendOpen(false)}
            />

            <ConfirmDialog
                open={!!del}
                title={T.deleteTitle}
                message={fill(T.deleteMessage, { company: del?.billing?.company ? fill(T.deleteMessageCompany, { company: del.billing.company }) : '' })}
                confirmLabel={T.delete}
                danger
                busy={delBusy}
                error={delError}
                onConfirm={confirmDelete}
                onCancel={() => { if (!delBusy) { setDel(null); setDelError('') } }}
            />
        </div>
    )
}
